import type { ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { Text } from '@/components/ui/Text';
import { useContextStore } from '@/lib/session/context-store';
import { useContextBootstrap } from '@/lib/session/use-context-bootstrap';
import { ContextPicker } from './ContextPicker';

function CenteredStatus({ children }: { children: ReactNode }) {
  return <div className="flex min-h-[70vh] w-full items-center justify-center p-6">{children}</div>;
}

/**
 * Gates the routed app on an active context (T17). The gateway reads the active
 * context from the server session (written by `switchContext`), so no
 * context-scoped query may run until one is active — this component renders the
 * picker / a spinner until then, keeping those queries from firing early
 * (handover #3) and turning `ACL_CONTEXT_REQUIRED` into a picker, not an error.
 */
export function ContextGate({ children }: { children: ReactNode }) {
  const active = useContextStore((s) => s.active);
  const { contexts, isLoading, isError, isSwitching, refetch, selectContext } = useContextBootstrap();

  // T0111 fix — a switch IN FLIGHT must gate children even when `active` is already set from a
  // PRIOR activation. `active` stays truthy for the entire life of the session after the first
  // switch resolves (only `clearActive()` nulls it), so the old `if (active) return children`
  // check — evaluated before this one — meant this branch was live for the FIRST switch only and
  // dead code for every switch after it: the app rendered straight through a switch already in
  // flight (server session mid-write, query cache mid-reset) with whatever was already on screen,
  // never showing "Switching context…" past the very first activation. Checked first now, so it
  // gates every switch, not just the first.
  if (isSwitching && contexts.length > 0) {
    return (
      <CenteredStatus>
        <div className="flex items-center gap-2 text-text-subtle">
          <Loader2 size={18} className="animate-spin" aria-hidden />
          <Text variant="body-md" color="muted">Switching context…</Text>
        </div>
      </CenteredStatus>
    );
  }

  // Active context resolved → render the app.
  if (active) return <>{children}</>;

  if (isLoading) {
    return (
      <CenteredStatus>
        <div className="flex items-center gap-2 text-text-subtle">
          <Loader2 size={18} className="animate-spin" aria-hidden />
          <Text variant="body-md" color="muted">Loading your workspaces…</Text>
        </div>
      </CenteredStatus>
    );
  }

  if (isError) {
    return (
      <CenteredStatus>
        <div className="text-center">
          <Text variant="body-md" weight="semibold" className="mb-1">Couldn't load your workspaces</Text>
          <button
            type="button"
            onClick={refetch}
            className="rounded-md text-body-sm font-medium text-primary outline-none hover:underline focus-visible:ring-2 focus-visible:ring-ring"
          >
            Retry
          </button>
        </div>
      </CenteredStatus>
    );
  }

  // No active context and nothing in flight → ask the user to choose.
  return <ContextPicker contexts={contexts} onSelect={selectContext} isSwitching={isSwitching} />;
}
